import { Text, Title } from "@clipboard-health/ui-components";
import { DialogContent, Skeleton, Stack } from "@mui/material";
import { usePostBatchReactions } from "@src/appV2/redesign/Workplace/Review/api/usePostBatchReactions";
import { useWorkplaceComments } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { PeerReviewSwipeDeck } from "@src/appV2/redesign/Workplace/Review/PeerReview/PeerReviewSwipeDeck";
import { type Reaction } from "@src/appV2/redesign/Workplace/Review/types";
import pluralize from "pluralize";
import { useCallback, useEffect, useRef } from "react";

import { type StepAction } from "../types";

const PEER_REVIEWS_PAGE_SIZE = 3;

interface PeerReviewsStepProps {
  workplaceId: string;
  workplaceName: string;
  onAction: (action: StepAction) => void;
}

export function PeerReviewsStep(props: PeerReviewsStepProps) {
  const { workplaceId, workplaceName, onAction } = props;

  const hasAutoSkipped = useRef(false);

  const { data: comments, isLoading } = useWorkplaceComments({
    workplaceId,
    filter: {
      excludeSelfComments: true,
    },
    page: { size: PEER_REVIEWS_PAGE_SIZE },
  });

  const { mutateAsync: postBatchReactions } = usePostBatchReactions({ workplaceId });

  const hasComments = comments && comments.length > 0;

  useEffect(() => {
    if (!isLoading && !hasComments && !hasAutoSkipped.current) {
      hasAutoSkipped.current = true;
      onAction({ type: "NEXT_STEP" });
    }
  }, [isLoading, hasComments, onAction]);

  const submitReactionsAndAdvance = useCallback(
    async (
      reactions: readonly { commentId: string; reaction: Reaction }[],
      actionType: "NEXT_STEP" | "SKIP_STEP"
    ) => {
      if (reactions.length > 0) {
        try {
          await postBatchReactions({ reactions: [...reactions] });
        } catch {
          // logging and error message is handled in the usePostBatchReactions hook
        }
      }

      onAction({ type: actionType });
    },
    [postBatchReactions, onAction]
  );

  const handleComplete = useCallback(
    async (reactions: readonly { commentId: string; reaction: Reaction }[]) => {
      await submitReactionsAndAdvance(reactions, "NEXT_STEP");
    },
    [submitReactionsAndAdvance]
  );

  const handleSkip = useCallback(
    async (reactions: readonly { commentId: string; reaction: Reaction }[]) => {
      await submitReactionsAndAdvance(reactions, "SKIP_STEP");
    },
    [submitReactionsAndAdvance]
  );

  if (isLoading) {
    return (
      <DialogContent>
        <Stack spacing={5} sx={{ flex: 1 }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={32} />
          <Skeleton variant="rounded" height={250} sx={{ borderRadius: 4 }} />
        </Stack>
      </DialogContent>
    );
  }

  if (!hasComments) {
    return null;
  }

  return (
    <DialogContent sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Stack spacing={3} sx={{ mb: 5 }}>
        <Text
          variant="caption"
          color={(theme) => theme.palette.text.secondary}
          sx={{ textTransform: "uppercase", letterSpacing: 1 }}
        >
          {workplaceName}
        </Text>
        <Title variant="h4" component="h2">
          Do these match your experience?
        </Title>
        <Text variant="body2">
          Swipe right to like, left to dislike. Just {pluralize("review", comments.length, true)}.
        </Text>
      </Stack>
      <PeerReviewSwipeDeck
        comments={comments}
        workplaceId={workplaceId}
        onComplete={handleComplete}
        onSkip={handleSkip}
      />
    </DialogContent>
  );
}
